import { Injectable } from '@angular/core';
import { LocalStorageService } from '../local-storage.service';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { User } from '../../../state/models/user.model';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { PersonEntityService } from '../../../state/entity-services/person-entity.service';
import { Person } from '../../../state/models/person';
import { Token } from '../../../state/models/token';
import { TokenEntityService } from '../../../state/entity-services/token-entity.service';
import * as Sentry from '@sentry/angular';
import { EntityManagementService } from '../../../state/entity-management/services/base/entity-management.service';

@Injectable({
  providedIn: 'root',
})
export class JwtAuthService {
  token: string | null = null;
  isAuthenticated: boolean = false;
  user: User | undefined = undefined;
  user$ = new BehaviorSubject<User | undefined>(this.user);
  signingIn: boolean = false;
  return: string = '';
  JWT_TOKEN = 'JWT_TOKEN';
  JWT_REFRESH_TOKEN = 'JWT_REFRESH_TOKEN';
  APP_USER = 'EGRET_USER';

  constructor(
    private entityManagementService: EntityManagementService,
    private tokenEntityService: TokenEntityService,
    private personEntityService: PersonEntityService,
    private ls: LocalStorageService,
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.route.queryParams.subscribe(
      params => (this.return = params['return'] || '/'),
    );
  }

  get isLoggedIn(): boolean {
    return !!this.getJwtToken();
  }

  public signInCodeGoogle(token: string): Observable<any> {
    const body = {
      grantType: 'GOOGLE_ACCESS_TOKEN',
      googleAccessToken: token,
    };
    this.signingIn = true;
    return this.getToken(body).pipe(
      switchMap(response => {
        return this.afterToken(response);
      })
    );
  }

  public signInCode(code: string, phone: string, document?: string): Observable<any> {
    this.signingIn = true;
    return this.authCode(code, phone, document).pipe(
      switchMap(response => {
        return this.afterToken(response);
      })
    );
  }


  afterToken(response: Token) {
    return of(response).pipe(
      tap((res) => {
        const { accessToken, refreshToken } = res as any;
        this.setUserAndToken({ accessToken, refreshToken });
      }),
      mergeMap((res: any) =>
        this.getSelf().pipe(
          tap(user => {
            const { accessToken, refreshToken } = res as any;
            this.setUserAndToken({ accessToken, refreshToken }, user, !!res);
            this.signingIn = false;
          }),
        ),
      ),
      mergeMap(() =>
        this.personEntityService.authInvitesAcceptAll().pipe()
      ),
      catchError(error => {
        return throwError(error);
      })
    );
  }

  /*
    checkTokenIsValid is called inside constructor of
    shared/components/layouts/admin-layout/admin-layout.component.ts
  */
  public checkTokenIsValid() {
    return this.getSelf().pipe(
      map((profile: User) => {
        this.setUserAndToken({ accessToken: this.getJwtToken() }, profile, true);
        return profile;
      }),
      catchError(error => {
        this.logout();
        return of(error);
      }),
    );
  }

  public logout(redirect?: boolean) {
    this.ls.removeItem(this.APP_USER);
    this.ls.removeItem(this.JWT_TOKEN);
    this.ls.removeItem(this.JWT_REFRESH_TOKEN);
    this.entityManagementService.clearAllCache();
    if (redirect) {
      this.router.navigate(['/sessions/login']);
    }
  }

  getJwtToken() {
    return this.ls.getItem(this.JWT_TOKEN);
  }

  getUser(): Person {
    return this.ls.getItem(this.APP_USER) as Person;
  }

  setUserAndToken(token: {
    accessToken: string,
    refreshToken?: string
  } | null, user?: User | null, isAuthenticated: boolean = false) {
    const { accessToken, refreshToken } = token || {};
    this.isAuthenticated = isAuthenticated;
    this.token = accessToken;
    this.user = user;
    this.ls.setItem(this.APP_USER, user);
    this.ls.setItem(this.JWT_TOKEN, accessToken);
    this.ls.setItem(this.JWT_REFRESH_TOKEN, refreshToken);
    this.user$.next(user);
    this.setUserSentry(true);
  }

  setUserSentry(force?: boolean): void {
    const { id, email, phone, document } = this.getUser() || {};
    if (!document && force === undefined) {
      return;
    }

    Sentry.setUser(
      {
        id,
        email,
        phone,
        document
      }
    );
  }

  getToken(body: any): Observable<Token> {
    return this.tokenEntityService.getTokenGeneral(body);
  }

  private authCode(code: string, phone: string, document?: string): Observable<Token> {
    return this.tokenEntityService.getToken(code, phone, document);
  }

  private getSelf(): Observable<User> {
    return this.personEntityService.getSelf();
  }

  refreshToken() {
    const body = {
      grantType: 'REFRESH_TOKEN',
      refreshToken: this.getJwtRefreshToken(),
    };
    return this.signIn(body).pipe(
      catchError(error => {
        return throwError(error);
      }),
    );
  }

  getJwtRefreshToken() {
    return this.ls.getItem(this.JWT_REFRESH_TOKEN);
  }


  signIn(body: any) {
    this.signingIn = true;
    return this.tokenEntityService.getTokenGeneral(body).pipe(
      switchMap(response => {
        return this.afterToken(response);
      })
    );
  }
}

interface AuthResponse {
  // The token
  accessToken: string;
  refreshToken: string;
}
